const ORDER_NOT_FOUND_MESSAGE = '未找到一致的订单，请核实车牌、手机号或订单编号'
const RETRY_FAILED_MESSAGE = '重试提交失败'

/** Hide only the known missing-order case; an unknown code always stays visible. */
export function visibleSyncErrors(errors = []) {
  return (Array.isArray(errors) ? errors : []).filter(error => {
    if (!error || typeof error !== 'object') return false
    if (error.error_code === 'ORDER_NOT_FOUND') return false
    // Empty/null codes are legacy records. Other codes take precedence over text.
    if (error.error_code != null && error.error_code !== '') return true
    return error.error_message !== ORDER_NOT_FOUND_MESSAGE
  })
}

/** Return the first visible failed job for each ID, preserving input order. */
export function retryableSyncJobs(errors = []) {
  const seen = new Set()
  return visibleSyncErrors(errors).filter(job => {
    const id = job.id
    const hasId = typeof id === 'string' ? id.trim() !== ''
      : typeof id === 'number' && Number.isFinite(id)
    if (job.state !== 'failed' || !hasId || seen.has(id)) return false
    seen.add(id)
    return true
  })
}

function isPermissionFailure(value) {
  return value?.status === 401 || value?.status === 403
    // The existing backend reports this permission error as HTTP 500.
    || value?.message === '需要管理员权限' || value?.error === '需要管理员权限'
}

function failureMessage(value) {
  return [value?.message, value?.error, value?.error_message, value]
    .find(message => typeof message === 'string' && message.trim() !== '')
    || RETRY_FAILED_MESSAGE
}

/**
 * Submit a fixed ID snapshot once each, strictly serially. `submitted` means
 * the retry was accepted (ok === true), NOT that the order has synchronized.
 * Progress counts attempted requests only; skipped jobs never emit progress.
 * Cancellation prevents the next request; it cannot undo an in-flight one.
 * Callers must guard UI updates in onProgress when their view has unmounted.
 */
export async function retrySyncJobs(jobs, retry, {
  onProgress = () => {},
  shouldContinue = () => true,
} = {}) {
  // Capture IDs as well as membership so later polling/input edits cannot
  // change which jobs this batch submits after its first await.
  const snapshot = retryableSyncJobs(jobs).map(job => ({ id: job.id }))
  const result = {
    total: snapshot.length,
    submitted: 0,
    failed: 0,
    skipped: 0,
    failures: [],
    stopped: false,
  }

  for (const job of snapshot) {
    if (!shouldContinue()) {
      result.stopped = true
      break
    }

    try {
      const response = await retry(job.id)
      const denied = isPermissionFailure(response)
      if (response?.ok === true && !denied) {
        result.submitted += 1
      } else {
        result.failed += 1
        result.failures.push({ id: job.id, message: failureMessage(response) })
        if (denied) result.stopped = true
      }
    } catch (error) {
      result.failed += 1
      result.failures.push({ id: job.id, message: failureMessage(error) })
      // A lost/forbidden session or ambiguous network outcome must not fan
      // out more writes. In particular, never automatically resend this ID.
      if (isPermissionFailure(error) || error instanceof TypeError) result.stopped = true
    }

    // Keep callback failures outside the request catch: UI code throwing a
    // TypeError is not evidence that the HTTP request failed.
    onProgress({ done: result.submitted + result.failed, total: result.total })
    if (result.stopped) break
  }

  result.skipped = result.total - result.submitted - result.failed
  return result
}
